import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Droplets, Clock, Footprints, MapPin, HeartPulse } from 'lucide-react';
import { FAQS, IMAGES, STUDIO, VIDEOS } from '../data/mock';
import { usePageMeta } from '../components/Layout';
import { Reveal, SectionHeader, PageHero, FAQAccordion, CloseCTA, PitVideo, VideoSeo } from '../components/Shared';

const CATS = ['All', 'The Workout', 'First Class', 'Pricing', 'Logistics'];

/* ============ /faq ============ */
export function FAQPage() {
  usePageMeta(
    'FAQ — Everything About PITFIT',
    'PITFIT FAQ: what a HIIT class is, how many calories you burn, what to bring, where to park at Travis Walk, and how much classes cost. $15 first class.'
  );

  const [cat, setCat] = useState('All');
  const faqs = cat === 'All' ? FAQS : FAQS.filter((f) => f.cat === cat);

  return (
    <>
      <PageHero
        tag="Answers"
        title="Everything people ask before their first class."
        sub="Straight answers to the real questions — cost, calories, parking, and whether you're “fit enough” to start. (You are.)"
      />

      <section className="section-pad !pt-8">
        <div className="max-w-3xl mx-auto px-5 lg:px-8">
          <div className="flex gap-2 flex-wrap mb-9" role="tablist" aria-label="FAQ category">
            {CATS.map((c) => (
              <button
                key={c}
                role="tab"
                aria-selected={cat === c}
                onClick={() => setCat(c)}
                className={`font-mono-pf text-[12px] uppercase tracking-[0.12em] px-4 py-2.5 min-h-[44px] rounded-full border transition-colors ${
                  cat === c ? 'text-white border-[rgba(125,167,255,0.6)]' : 'text-muted-pf border-[rgba(148,170,220,0.2)] hover:text-white'
                }`}
                style={cat === c ? { background: 'rgba(70,127,247,0.16)' } : {}}
              >
                {c}
              </button>
            ))}
          </div>
          <FAQAccordion faqs={faqs} />
          <p className="text-muted-pf text-[15px] mt-9 text-center">
            Still curious? <Link to="/contact" className="underline underline-offset-4" style={{ color: 'var(--pf-brand2)' }}>Ask us directly</Link> — or better, just come feel it.
          </p>
        </div>
      </section>

      <CloseCTA />
    </>
  );
}

/* ============ /hiit-workout/first-class ============ */
export function FirstClass() {
  usePageMeta(
    'Your First Class — What to Expect',
    'Coming to your first PITFIT class? What to bring, where to park at Travis Walk, when to arrive, and exactly what the 60 minutes look like. $15 first class.'
  );

  const steps = [
    { icon: MapPin, t: 'Park in the garage', d: 'Enter the Travis Walk Plaza garage from Travis Street. Proceed to the lower 3rd level and park in any open spot — it\u2019s free — then follow the PITFIT signs to the studio.' },
    { icon: Clock, t: 'Arrive 15 minutes early', d: 'Check in, meet your coach, and get a walkthrough of the floor stations and how the blocks rotate.' },
    { icon: HeartPulse, t: 'Work at your level', d: 'You control your pace and your weights. The coach gives a beginner option for every movement — intensity is personal.' },
    { icon: Droplets, t: 'Finish strong', d: 'Class closes with mobility work. You\u2019ll be soaked, buzzing, and already thinking about your next one. That\u2019s normal.' },
  ];

  const bring = [
    { icon: Droplets, t: 'Water bottle', d: 'Refill stations on site' },
    { icon: Footprints, t: 'Training shoes', d: 'Good grip for floor work + circuits' },
  ];

  const firstFaqs = FAQS.filter((f) => f.cat === 'First Class');

  return (
    <>
      <VideoSeo keys={['arrive', 'intensity']} />
      <PageHero
        tag="First-Timers"
        title="Your first class, minute by minute."
        sub="Everyone in the PIT had a first day. Here's exactly how yours goes — from the parking garage to the final stretch."
        video={VIDEOS.arrive}
      />

      <section className="section-pad">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <SectionHeader tag="How It Goes" title="Four steps. Zero guesswork." />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {steps.map((s, i) => (
              <Reveal key={s.t} delay={i * 80}>
                <div className="glass-card glass-card-hover p-7 h-full">
                  <div className="flex items-center justify-between mb-5">
                    <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: 'rgba(70,127,247,0.12)', border: '1px solid rgba(125,167,255,0.3)' }}>
                      <s.icon size={20} style={{ color: 'var(--pf-brand2)' }} />
                    </div>
                    <span className="font-mono-pf text-[13px] text-muted-pf">0{i + 1}</span>
                  </div>
                  <h3 className="font-display font-semibold text-white text-lg">{s.t}</h3>
                  <p className="text-muted-pf text-[14.5px] mt-2.5 leading-relaxed">{s.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section-pad" style={{ background: 'var(--pf-bg2)' }}>
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <SectionHeader tag="Pack Light" title="What to bring" sub="That's the whole list. We handle everything else — equipment, programming, and the push you didn't know you had." />
            <div className="space-y-4">
              {bring.map((b) => (
                <div key={b.t} className="glass-card p-5 flex items-center gap-5">
                  <b.icon size={22} className="shrink-0" style={{ color: 'var(--pf-brand2)' }} />
                  <div>
                    <p className="font-display font-semibold text-white">{b.t}</p>
                    <p className="text-muted-pf text-[14px]">{b.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={120}>
            <div className="rounded-2xl overflow-hidden border border-[rgba(148,170,220,0.16)]">
              <PitVideo video={VIDEOS.intensity} className="w-full h-[420px] object-cover" label="A first-time member pushing through a conditioning block inside the PITFIT garage" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="section-pad">
        <div className="max-w-3xl mx-auto px-5 lg:px-8">
          <SectionHeader tag="First-Class FAQ" title="The last-minute questions" center />
          <FAQAccordion faqs={firstFaqs} />
        </div>
      </section>

      <CloseCTA title="Ready? Your first hour is $15." sub={`No membership, no commitment. ${STUDIO.address} — we'll see you 15 minutes early.`} />
    </>
  );
}

/* ============ /hiit-workout/beginners ============ */
export function Beginners() {
  usePageMeta(
    'HIIT for Beginners — Is It Right for You?',
    'Is HIIT good for beginners? Yes — here\u2019s how PITFIT scales every class to your level, what beginners should expect, and how to start smart. $15 first class.'
  );

  const myths = [
    { m: '“I need to get in shape before I start.”', r: 'The class gets you in shape — that\u2019s its job. You set your own pace and pick your own weights. Day one is about finishing, not winning.' },
    { m: '“HIIT is only for athletes.”', r: 'Our members range from college students to a 63-year-old who calls it \u201cthe calorie killer.\u201d Intensity is relative — your 100% is the only one that matters.' },
    { m: '“I\u2019ll be lost in a group class.”', r: 'Every class runs with a dedicated coach and an intentionally sized room. Tell them it\u2019s your first class and they\u2019ll demo every movement and check on you all hour.' },
    { m: '“I\u2019ll be too sore to function.”', r: 'Expect honest soreness after class one — then watch it fade as you adapt. The weekly split is designed so muscle groups always get recovery days.' },
  ];

  const beginnerFaqs = FAQS.filter((f) => ['Are HIIT classes good for beginners?', 'How often should I do HIIT?', 'Do I need to be in shape before I start?'].includes(f.q));

  return (
    <>
      <VideoSeo keys={['cardio']} />
      <PageHero
        tag="Beginners Welcome"
        title="Is HIIT right for me? Short answer: yes."
        sub="Every single member of the 6 AM crew was a beginner once. Here's what starting actually looks like — minus the intimidation."
        video={VIDEOS.cardio}
      />

      <section className="section-pad">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <SectionHeader tag="Myth vs. Reality" title="The four fears that keep people out of the PIT." />
          <div className="grid md:grid-cols-2 gap-5">
            {myths.map((x, i) => (
              <Reveal key={x.m} delay={i * 70}>
                <div className="glass-card glass-card-hover p-7 h-full">
                  <p className="font-display font-semibold text-[17px]" style={{ color: 'var(--pf-brand2)' }}>{x.m}</p>
                  <p className="text-muted-pf text-[15px] mt-3 leading-relaxed">{x.r}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section-pad" style={{ background: 'var(--pf-bg2)' }}>
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <SectionHeader tag="Start Smart" title="Your first month, mapped." />
          <div className="grid md:grid-cols-3 gap-5">
            {[
              ['Week 1', 'One class', 'Book your first class. Goal: finish, learn the format, feel the room. Nothing else matters yet.'],
              ['Weeks 2–3', 'Two per week', 'Use your 2 weeks unlimited. Try different days of the split — upper, lower, full-body — and find your favorite coach.'],
              ['Week 4+', 'Three to four', 'This is where the transformation math starts working. Most members settle at 3–4 classes weekly.'],
            ].map(([w, t, d], i) => (
              <Reveal key={w} delay={i * 80}>
                <div className="glass-card p-7 h-full">
                  <p className="mono-label">{w}</p>
                  <h3 className="font-display font-semibold text-white text-xl mt-2">{t}</h3>
                  <p className="text-muted-pf text-[15px] mt-3 leading-relaxed">{d}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="flex flex-wrap gap-3 mt-10">
            <Link to="/book" className="btn-primary">Book Your First Class <ArrowRight size={16} /></Link>
            <Link to="/hiit-workout/first-class" className="btn-ghost">First-Class Guide</Link>
          </div>
        </div>
      </section>

      <section className="section-pad">
        <div className="max-w-3xl mx-auto px-5 lg:px-8">
          <SectionHeader tag="Beginner FAQ" title="Asked by every first-timer" center />
          <FAQAccordion faqs={beginnerFaqs} />
        </div>
      </section>

      <CloseCTA title="Beginners start at $15." sub="No membership, no judgment, no fitness prerequisites. Just show up and work at your pace." />
    </>
  );
}
